import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Cookies from "js-cookie";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useHistory } from "react-router-dom";
import "./analytics-rebrand.css";

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;

const emptyAnalytics = {
  forecastRows: [],
  paid: { totalLoanAmount: 0, totalRepayPrincipal: 0, percentage: 0 },
  interestRows: [],
  totalInterestProfit: 0,
  marketRows: [],
  marketStats: { current: 0, low: 0, high: 0 },
  industryRows: [],
  averagePrincipal: 0,
};

const compactCurrency = (value) => {
  const amount = Number(value) || 0;

  if (Math.abs(amount) >= 1000000) {
    return `${(amount / 1000000).toFixed(amount % 1000000 === 0 ? 0 : 1)}M`;
  }

  if (Math.abs(amount) >= 1000) {
    return `${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 0)}K`;
  }

  return amount.toFixed(0);
};

const formatCurrency = (value) =>
  `${compactCurrency(value)}`.replace("NaN", "0");

const formatPercent = (value) => {
  const numeric = Number(value) || 0;
  return `${numeric.toFixed(1)}%`;
};

const getExportRange = (dateRange) => {
  if (dateRange && dateRange[0] && dateRange[1]) {
    return {
      startDate: dateRange[0].startOf("day").format("YYYY-MM-DD"),
      endDate: dateRange[1].endOf("day").format("YYYY-MM-DD"),
    };
  }

  return {
    startDate: moment().startOf("month").format("YYYY-MM-DD"),
    endDate: moment().endOf("month").format("YYYY-MM-DD"),
  };
};

const readDate = (item) => item?.Date || item?.date || item?.created_at;

const isWithinRange = (item, dateRange) => {
  if (!dateRange || !dateRange[0] || !dateRange[1]) {
    return true;
  }

  const date = moment(readDate(item)).format("YYYY-MM-DD");
  const startDate = dateRange[0].startOf("day").format("YYYY-MM-DD");
  const endDate = dateRange[1].endOf("day").format("YYYY-MM-DD");

  return moment(date).isBetween(startDate, endDate, "day", "[]");
};

const monthLabel = (date) => moment(date).format("MMM");

const getPaddedDomain = (values, forceZero = false) => {
  const numericValues = values.map((value) => Number(value) || 0);
  const rawMin = forceZero ? 0 : Math.min(...numericValues, 0);
  const rawMax = Math.max(...numericValues, 1);
  const span = rawMax - rawMin || rawMax || 1;

  return {
    min: forceZero ? 0 : rawMin - span * 0.14,
    max: rawMax + span * 0.14,
  };
};

const buildLinePoints = (rows, key, width, height, padding, domain) => {
  const usableWidth = width - padding.left - padding.right;
  const usableHeight = height - padding.top - padding.bottom;
  const span = domain.max - domain.min || 1;

  return rows.map((row, index) => {
    const x =
      rows.length === 1
        ? padding.left + usableWidth / 2
        : padding.left + (index / (rows.length - 1)) * usableWidth;
    const y =
      padding.top +
      usableHeight -
      ((Number(row[key]) || 0) - domain.min) / span * usableHeight;
    return { x, y };
  });
};

const buildLinearPath = (points) => {
  return points.reduce((path, point, index) => {
    if (index === 0) {
      return `M${point.x} ${point.y}`;
    }

    const previous = points[index - 1];
    const midX = (previous.x + point.x) / 2;
    return `${path} C${midX} ${previous.y} ${midX} ${point.y} ${point.x} ${point.y}`;
  }, "");
};

const buildAreaPath = (points, height, padding) => {
  if (points.length < 2) {
    return "";
  }

  const linePath = buildLinearPath(points);
  const baseline = height - padding.bottom;
  const firstX = points[0].x;
  const lastX = points[points.length - 1].x;

  return `${linePath} L${lastX} ${baseline} L${firstX} ${baseline} Z`;
};

const monthKey = (item) => moment(readDate(item)).format("YYYY-MM");

const transformForecastRows = (amountReceived = [], loanAmount = [], dateRange) => {
  const rowsByMonth = new Map();

  amountReceived.filter((item) => isWithinRange(item, dateRange)).forEach((item) => {
    const date = monthKey(item);
    const row = rowsByMonth.get(date) || { date, repayAmount: 0, loanAmount: 0 };
    row.repayAmount += Number(item.repay_amount) || 0;
    rowsByMonth.set(date, row);
  });

  loanAmount.filter((item) => isWithinRange(item, dateRange)).forEach((item) => {
    const date = monthKey(item);
    const row = rowsByMonth.get(date) || { date, repayAmount: 0, loanAmount: 0 };
    row.loanAmount += Number(item.amount) || 0;
    rowsByMonth.set(date, row);
  });

  let cumulativeLoanAmount = 0;
  let cumulativeRepayAmount = 0;

  return Array.from(rowsByMonth.values())
    .sort((a, b) => moment(a.date) - moment(b.date))
    .map((row) => {
      cumulativeLoanAmount += row.loanAmount;
      cumulativeRepayAmount += row.repayAmount;

      return {
        ...row,
        loanAmount: cumulativeLoanAmount,
        repayAmount: cumulativeRepayAmount,
      };
    });
};

const transformPaidStats = (amountReceived = [], loanAmount = [], dateRange) => {
  const filteredReceived = amountReceived.filter((item) =>
    isWithinRange(item, dateRange)
  );
  const filteredLoans = loanAmount.filter((item) => isWithinRange(item, dateRange));
  const totalRepayPrincipal = filteredReceived.reduce(
    (sum, item) => sum + (Number(item.repay_principal) || 0),
    0
  );
  const totalLoanAmount = filteredLoans.reduce(
    (sum, item) => sum + (Number(item.amount) || 0),
    0
  );
  const percentage = totalLoanAmount
    ? (totalRepayPrincipal / totalLoanAmount) * 100
    : 0;

  return { totalLoanAmount, totalRepayPrincipal, percentage };
};

const transformInterestRows = (items = [], dateRange) => {
  const rowsByMonth = new Map();

  items.filter((item) => isWithinRange(item, dateRange)).forEach((item) => {
    const date = monthKey(item);
    const row = rowsByMonth.get(date) || { date, repayInterest: 0 };
    row.repayInterest += Number(item.repay_interest) || 0;
    rowsByMonth.set(date, row);
  });

  return Array.from(rowsByMonth.values()).sort(
    (a, b) => moment(a.date) - moment(b.date)
  );
};

const transformMarketRows = (items = [], dateRange) => {
  const rowsByMonth = new Map();

  items
    .filter((item) => isWithinRange(item, dateRange))
    .forEach((item) => {
      const date = monthKey(item);
      const row = rowsByMonth.get(date) || { date, total: 0, count: 0 };
      row.total +=
        (Number(item.interest_rate) || 0) <= 1
          ? (Number(item.interest_rate) || 0) * 100
          : Number(item.interest_rate) || 0;
      row.count += 1;
      rowsByMonth.set(date, row);
    });

  return Array.from(rowsByMonth.values())
    .map((row) => ({
      date: row.date,
      interestRate: row.count ? row.total / row.count : 0,
    }))
    .sort((a, b) => moment(a.date) - moment(b.date));
};

const transformIndustryRows = (items = [], dateRange) => {
  const counts = items
    .filter((item) => isWithinRange(item, dateRange))
    .reduce((acc, item) => {
      const sector = item.employment_sector || "Unspecified";
      acc[sector] = (acc[sector] || 0) + 1;
      return acc;
    }, {});
  const total = Object.values(counts).reduce((sum, value) => sum + value, 0);

  return Object.entries(counts)
    .map(([label, count]) => ({
      label,
      count,
      percentage: total ? (count / total) * 100 : 0,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 4);
};

const ForecastRevenueVisual = ({ rows }) => {
  const width = 760;
  const height = 260;
  const padding = { top: 40, right: 30, bottom: 34, left: 40 };
  const domain = getPaddedDomain(
    rows.flatMap((row) => [row.loanAmount, row.repayAmount]),
    true
  );
  const loanPoints = buildLinePoints(
    rows,
    "loanAmount",
    width,
    height,
    padding,
    domain
  );
  const repayPoints = buildLinePoints(
    rows,
    "repayAmount",
    width,
    height,
    padding,
    domain
  );
  const loanPath = buildLinearPath(loanPoints);
  const repayPath = buildLinearPath(repayPoints);
  const areaPath = buildAreaPath(loanPoints, height, padding);
  const markerIndexes = [...new Set([0, Math.floor(rows.length / 2), rows.length - 1])]
    .filter((index) => index >= 0 && rows[index]);

  if (rows.length < 2) {
    return <div className="sl-analytics-empty">No data to show.</div>;
  }

  return (
    <svg viewBox="0 0 760 260" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <linearGradient id="analyticsRevFill" x1="0" x2="0" y1="0" y2="1">
        <stop stopColor="#7057F6" stopOpacity=".28" />
        <stop offset="1" stopColor="#7057F6" stopOpacity="0" />
      </linearGradient>
    </defs>
    <g stroke="#E4E7F6" strokeWidth="1">
      <path d="M40 40H730" />
      <path d="M40 95H730" />
      <path d="M40 150H730" />
      <path d="M40 205H730" />
    </g>
    <path d={areaPath} fill="url(#analyticsRevFill)" />
    <path d={loanPath} fill="none" stroke="#7057F6" strokeLinecap="round" strokeWidth="6" />
    <path d={repayPath} fill="none" stroke="#285B9F" strokeLinecap="round" strokeWidth="6" />
    <g fill="#fff" stroke="#7057F6" strokeWidth="5">
      {markerIndexes.map((index) => {
        return (
          <circle
            key={rows[index].date}
            cx={loanPoints[index].x}
            cy={loanPoints[index].y}
            r="8"
          />
        );
      })}
    </g>
    <g fill="#98A2B7" fontSize="10" fontWeight="800">
      {markerIndexes.map((index) => {
        const x =
          rows.length === 1
            ? padding.left + (width - padding.left - padding.right) / 2
            : padding.left +
              (index / (rows.length - 1)) *
                (width - padding.left - padding.right);

        return (
          <text key={`${rows[index].date}-label`} x={x - 10} y="256">
            {monthLabel(rows[index].date)}
          </text>
        );
      })}
    </g>
  </svg>
  );
};

const PaidPrincipalVisual = ({ percentage }) => (
  <div className="sl-analytics-donut-wrap">
    <svg viewBox="0 0 120 120" aria-hidden="true">
      <circle
        cx="60"
        cy="60"
        r="48"
        fill="none"
        stroke="#EEF1FB"
        strokeWidth="16"
      />
      <circle
        cx="60"
        cy="60"
        r="48"
        fill="none"
        stroke="#7057F6"
        strokeDasharray={`${Math.max(0, Math.min(100, percentage)) * 3.02} 302`}
        strokeLinecap="round"
        strokeWidth="16"
        transform="rotate(-90 60 60)"
      />
    </svg>
    <div className="sl-analytics-donut-label">
      <strong>{Math.round(percentage)}%</strong>
      <span>received</span>
    </div>
  </div>
);

const InterestProfitVisual = ({ rows }) => {
  const path = buildLinearPath(
    buildLinePoints(
      rows,
      "repayInterest",
      260,
      96,
      { top: 14, right: 6, bottom: 16, left: 6 },
      getPaddedDomain(
        rows.map((row) => row.repayInterest),
        false
      )
    )
  );

  if (rows.length < 2) {
    return <div className="sl-analytics-empty sl-analytics-empty-dark">No data</div>;
  }

  return (
    <svg viewBox="0 0 260 96" preserveAspectRatio="none" aria-hidden="true">
      <path d={path} fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="6" />
      <path d={path} fill="none" stroke="#8B6CFF" strokeLinecap="round" strokeWidth="2" />
    </svg>
  );
};

const MarketRateVisual = ({ rows }) => {
  const width = 520;
  const height = 120;
  const padding = { top: 20, right: 16, bottom: 20, left: 12 };
  const domain = getPaddedDomain(
    rows.map((row) => row.interestRate),
    false
  );
  const points = buildLinePoints(
    rows,
    "interestRate",
    width,
    height,
    padding,
    domain
  );
  const path = buildLinearPath(points);
  const markerIndexes = [...new Set([0, Math.floor(rows.length / 2), rows.length - 1])]
    .filter((index) => index >= 0 && rows[index]);

  if (rows.length < 2) {
    return <div className="sl-analytics-empty">No data to show.</div>;
  }

  return (
    <svg viewBox="0 0 520 120" preserveAspectRatio="none" aria-hidden="true">
      <path d={path} fill="none" stroke="#7057F6" strokeLinecap="round" strokeWidth="7" />
      <g fill="#fff" stroke="#7057F6" strokeWidth="5">
        {markerIndexes.map((index) => {
          return (
            <circle
              key={rows[index].date}
              cx={points[index].x}
              cy={points[index].y}
              r="7"
            />
          );
        })}
      </g>
    </svg>
  );
};

const Analytics = () => {
  const [dateRange, setDateRange] = useState([]);
  const [analytics, setAnalytics] = useState(emptyAnalytics);
  const [email, setEmail] = useState("");
  const history = useHistory();

  useEffect(() => {
    const isAuthenticated = Cookies.get("token");
    const userAccountType = Cookies.get("user_account_type");

    if (!isAuthenticated) {
      history.push("/auth/signin");
    } else if (userAccountType === "borrower") {
      history.push("/b-dashboard");
    }
  }, [history]);

  useEffect(() => {
    const fetchUser = async () => {
      const response = await fetch(
        "https://www.smart-lend.com/api/auth/register/viewUser",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );
      const json = await response.json();
      setEmail(json.email || "");
    };

    fetchUser();
  }, []);

  useEffect(() => {
    const authHeaders = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${Cookies.get("token")}`,
    };

    const fetchJson = async (path) => {
      const response = await fetch(`https://www.smart-lend.com/api/${path}`, {
        headers: authHeaders,
      });
      return response.json();
    };

    const fetchAnalytics = async () => {
      try {
        const [
          forecast,
          paidPrincipal,
          interestProfit,
          marketInterest,
          industries,
          averagePrincipal,
        ] = await Promise.all([
          fetchJson("analytics/forecastRevenue"),
          fetchJson("analytics/analyticsPaidPrincipal"),
          fetchJson("analytics/interestProfit"),
          fetchJson("analytics/analyticsMarketInterestRate"),
          fetchJson("analytics/industryAnalytics"),
          fetchJson("analytics/analyticsAvgPrincipalAmount"),
        ]);

        const forecastRows = transformForecastRows(
          forecast["Amount Received"] || [],
          forecast["Loan Amount"] || [],
          dateRange
        );
        const paid = transformPaidStats(
          paidPrincipal["Amount Received"] || [],
          paidPrincipal["Loan Amount"] || [],
          dateRange
        );
        const interestRows = transformInterestRows(
          interestProfit["Interest Profit"] || [],
          dateRange
        );
        const totalInterestProfit = interestRows.reduce(
          (sum, row) => sum + row.repayInterest,
          0
        );
        const marketRows = transformMarketRows(
          marketInterest["All Interest Rate"] || [],
          dateRange
        );
        const marketValues = marketRows.map((row) => row.interestRate);
        const marketStats = {
          current: marketValues.length
            ? marketValues.reduce((sum, value) => sum + value, 0) /
              marketValues.length
            : 0,
          low: marketValues.length ? Math.min(...marketValues) : 0,
          high: marketValues.length ? Math.max(...marketValues) : 0,
        };
        const principalRows = (averagePrincipal["All Principal Amount"] || [])
          .filter((item) => isWithinRange(item, dateRange))
          .map((item) => Number(item.amount) || 0);

        setAnalytics({
          forecastRows,
          paid,
          interestRows,
          totalInterestProfit,
          marketRows,
          marketStats,
          industryRows: transformIndustryRows(
            industries["Industry Analytics"] || [],
            dateRange
          ),
          averagePrincipal: principalRows.length
            ? principalRows.reduce((sum, value) => sum + value, 0) /
              principalRows.length
            : 0,
        });
      } catch (error) {
        console.error(error);
      }
    };

    fetchAnalytics();
  }, [dateRange]);

  const handleExportClick = async () => {
    try {
      const { startDate, endDate } = getExportRange(dateRange);
      const response = await fetch(
        `https://www.smart-lend.com/api/analytics/exportExcel?start_date=${startDate}&end_date=${endDate}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            Accept: "application/json",
          },
        }
      );
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${email}_Analytics_${startDate}_${endDate}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <main className="sl-analytics-page">
      <section className="sl-analytics-frame">
        <div className="sl-analytics-head">
          <div>
            <h1>Analytics</h1>
            <p>
              Portfolio performance, collections, market rates, and industry
              exposure.
            </p>
          </div>
          <div className="sl-analytics-toolbar">
            <span>
              <b>View by:</b>
            </span>
            <RangePicker onChange={setDateRange} value={dateRange} />
            <button
              className="sl-analytics-btn sl-analytics-btn-primary"
              type="button"
              onClick={handleExportClick}
            >
              <DownloadOutlinedIcon fontSize="small" />
              Export
            </button>
          </div>
        </div>

        <section className="sl-analytics-kpis" aria-label="Analytics summary">
          <article className="sl-analytics-card">
            <span className="sl-analytics-label">Total principal</span>
            <div className="sl-analytics-amount">
              <span>SGD</span>
              {formatCurrency(analytics.paid.totalLoanAmount)}
            </div>
            <p>Disbursed portfolio</p>
          </article>
          <article className="sl-analytics-card">
            <span className="sl-analytics-label">Collected</span>
            <div className="sl-analytics-amount">
              <span>SGD</span>
              {formatCurrency(analytics.paid.totalRepayPrincipal)}
            </div>
            <p>{Math.round(analytics.paid.percentage)}% principal received</p>
          </article>
          <article className="sl-analytics-card sl-analytics-card-dark">
            <span className="sl-analytics-label">Market rate</span>
            <div className="sl-analytics-amount">
              {formatPercent(analytics.marketStats.current)}
            </div>
            <p>Current blended benchmark</p>
          </article>
          <article className="sl-analytics-card">
            <span className="sl-analytics-label">Avg. loan size</span>
            <div className="sl-analytics-amount">
              <span>SGD</span>
              {formatCurrency(analytics.averagePrincipal)}
            </div>
            <p>Per active borrower</p>
          </article>
        </section>

        <section className="sl-analytics-mosaic">
          <article className="sl-analytics-card sl-analytics-chart-card">
            <div className="sl-analytics-chart-head">
              <div>
                <h2>Forecast Revenue</h2>
                <p>Expected collections versus received amount</p>
              </div>
              <div className="sl-analytics-legend">
                <span>
                  <i />
                  Loaned
                </span>
                <span>
                  <i className="sl-analytics-legend-blue" />
                  Received
                </span>
              </div>
            </div>
            <div className="sl-analytics-line-chart">
              <ForecastRevenueVisual rows={analytics.forecastRows} />
            </div>
            <div className="sl-analytics-metric-row">
              <div className="sl-analytics-metric-pill">
                <span className="sl-analytics-label">Projected</span>
                <strong>
                  SGD{" "}
                  {formatCurrency(
                    analytics.forecastRows.reduce(
                      (sum, row) => sum + row.loanAmount,
                      0
                    )
                  )}
                </strong>
              </div>
              <div className="sl-analytics-metric-pill">
                <span className="sl-analytics-label">Received</span>
                <strong>
                  SGD{" "}
                  {formatCurrency(
                    analytics.forecastRows.reduce(
                      (sum, row) => sum + row.repayAmount,
                      0
                    )
                  )}
                </strong>
              </div>
              <div className="sl-analytics-metric-pill">
                <span className="sl-analytics-label">Variance</span>
                <strong className="sl-analytics-positive">
                  {formatPercent(analytics.paid.percentage)}
                </strong>
              </div>
            </div>
          </article>

          <aside className="sl-analytics-side-grid">
            <article className="sl-analytics-card">
              <div className="sl-analytics-chart-head">
                <div>
                  <h2>Paid Principal</h2>
                  <p>Collection completion</p>
                </div>
                <span className="sl-analytics-delta">On track</span>
              </div>
              <div className="sl-analytics-donut-chart">
                <PaidPrincipalVisual percentage={analytics.paid.percentage} />
              </div>
            </article>

            <article className="sl-analytics-card sl-analytics-card-dark">
              <div className="sl-analytics-chart-head">
                <div>
                  <h2>Interest Profit</h2>
                  <p>Month-on-month movement</p>
                </div>
              </div>
              <div className="sl-analytics-profit-chart">
                <InterestProfitVisual rows={analytics.interestRows} />
              </div>
              <div className="sl-analytics-amount">
                <span>SGD</span>
                {formatCurrency(analytics.totalInterestProfit)}
              </div>
            </article>
          </aside>
        </section>

        <section className="sl-analytics-bottom-grid">
          <article className="sl-analytics-card">
            <div className="sl-analytics-chart-head">
              <div>
                <h2>Market Interest Rate</h2>
                <p>Benchmark rate across active opportunities</p>
              </div>
              <span className="sl-analytics-delta">Stable</span>
            </div>
            <div className="sl-analytics-mini-chart">
              <MarketRateVisual rows={analytics.marketRows} />
            </div>
            <div className="sl-analytics-metric-row">
              <div className="sl-analytics-metric-pill">
                <span className="sl-analytics-label">Current</span>
                <strong>{formatPercent(analytics.marketStats.current)}</strong>
              </div>
              <div className="sl-analytics-metric-pill">
                <span className="sl-analytics-label">Low</span>
                <strong>{formatPercent(analytics.marketStats.low)}</strong>
              </div>
              <div className="sl-analytics-metric-pill">
                <span className="sl-analytics-label">High</span>
                <strong>{formatPercent(analytics.marketStats.high)}</strong>
              </div>
            </div>
          </article>

          <article className="sl-analytics-card">
            <div className="sl-analytics-chart-head">
              <div>
                <h2>Industries Analytics</h2>
                <p>Exposure concentration by borrower industry</p>
              </div>
            </div>
            <div className="sl-analytics-industry-bars">
              {analytics.industryRows.map((row) => (
                <React.Fragment key={row.label}>
                  <span>{row.label}</span>
                  <div className="sl-analytics-industry-track">
                    <i style={{ width: `${Math.max(row.percentage, 4)}%` }} />
                  </div>
                  <strong>{Math.round(row.percentage)}%</strong>
                </React.Fragment>
              ))}
              {analytics.industryRows.length === 0 && (
                <div className="sl-analytics-empty">No data to show.</div>
              )}
            </div>
          </article>
        </section>
      </section>
    </main>
  );
};

export default Analytics;
